import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { LinkMarkStyle } from '@model-framework/link';
import { LinkMarkStyleMenuIcon } from '@model-framework/link/LinkMarkStyle/LinkMarkStyleMenuView/LinkMarkStyleMenuIcon';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
type Props = {
  onMarkStyleSelected(markStyle: LinkMarkStyle): void;
  onOpenSubMenu(menuType: string): void;
};

export const LinkMarkStyleMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onMarkStyleSelected, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('markStyle');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.linkType')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-32 top-0 z-50 w-32 rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.NoMark)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.NoMark} isSelected={false} />
            </g>
          </svg>
        </button>
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.Arrow)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.Arrow} isSelected={false} />
            </g>
          </svg>
        </button>
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.TwoWayArrow)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.TwoWayArrow} isSelected={false} />
            </g>
          </svg>
        </button>
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.Diamond)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.Diamond} isSelected={false} />
            </g>
          </svg>
        </button>
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.HollowDiamond)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.HollowDiamond} isSelected={false} />
            </g>
          </svg>
        </button>
        <button
          className="flex w-full cursor-pointer px-8 py-4 hover:bg-gray-300"
          onClick={() => onMarkStyleSelected(LinkMarkStyle.HollowTriangle)}
        >
          <svg viewBox="0 0 32 32">
            <g transform={`translate(16, 16)`}>
              <LinkMarkStyleMenuIcon style={LinkMarkStyle.HollowTriangle} isSelected={false} />
            </g>
          </svg>
        </button>
      </div>
    </div>
  );
});

LinkMarkStyleMenuButton.displayName = 'LinkMarkStyleMenuButton';
